import { useEffect, useState } from "react";
import Footer from "../globalComponents/footer";

const apiBaseUrl =
  process.env.REACT_APP_API_URL ||
  "https://qvduta4gg3.execute-api.us-east-2.amazonaws.com/dev";

const rolesCacheKey = "gridElevatedCareerRoles";
const rolesCacheTtlMs = 10 * 60 * 1000;

// Keep the form reset value in one place so success/reset can restore it cleanly.
const initialForm = {
  name: "",
  email: "",
  phone: "",
  country: "United States",
  city: "",
  workAuthorization: "",
  whyWorkHere: "",
  privacyConsent: false,
};

function roleIdentifierFromTitle(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function readCachedRoles() {
  if (typeof window === "undefined") return null;

  try {
    // Use sessionStorage so returning to the careers page does not re-fetch roles every time.
    const cached = window.sessionStorage.getItem(rolesCacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    if (!Array.isArray(parsed.roles) || typeof parsed.savedAt !== "number") {
      return null;
    }

    const roles = parsed.roles
      .map((role) => {
        const title = roleText(role.title);
        const identifier =
          typeof role.identifier === "string" && role.identifier
            ? role.identifier
            : roleIdentifierFromTitle(title);

        if (!title || !identifier) return null;

        return {
          ...role,
          title,
          identifier,
        };
      })
      .filter(Boolean);

    return {
      roles,
      isStale: Date.now() - parsed.savedAt > rolesCacheTtlMs,
    };
  } catch {
    return null;
  }
}

function writeCachedRoles(roles) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      rolesCacheKey,
      JSON.stringify({
        roles,
        savedAt: Date.now(),
      })
    );
  } catch {
    // Ignore storage failures; the live API response is still enough.
  }
}

function roleText(value) {
  // Monday/API fields may come back as plain strings or small objects depending on the column type.
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";

  return value.name || value.label || value.text || value.title || "";
}

function getSelectedRole(roles, roleIdentifier) {
  // The URL stores a role identifier; fall back to the first role when no URL role is present.
  return roles.find((role) => role.identifier === roleIdentifier) || roles[0] || null;
}

function sectionLines(value) {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function sectionBullets(value) {
  // If the job text starts lines with bullets, render a real <ul>; otherwise keep paragraph text.
  const lines = sectionLines(value);
  const bulletLines = [];

  for (const line of lines) {
    if (/^[-*\u2022]\s*/.test(line)) {
      const item = line.replace(/^[-*\u2022]\s*/, "").trim();
      if (item) bulletLines.push(item);
      continue;
    }

    if (bulletLines.length) {
      // Continuation lines belong to the previous bullet.
      bulletLines[bulletLines.length - 1] = `${bulletLines[bulletLines.length - 1]} ${line}`;
    }
  }

  return bulletLines;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      // FileReader gives us text like "data:application/pdf;base64,JVBER...".
      // The API only needs the part after the comma, so we strip the prefix off.
      const result = typeof reader.result === "string" ? reader.result : "";
      resolve(result.includes(",") ? result.split(",").pop() : result);
    };
    reader.onerror = () => reject(new Error("Resume could not be read."));
    reader.readAsDataURL(file);
  });
}

function Icon({ type, className = "size-4" }) {
  const paths = {
    arrow: "M13.5 4.5H19.5V10.5M19 5L10 14M19.5 4.5L19.5 10.5",
    briefcase:
      "M10 6V5A2 2 0 0 1 12 3H16A2 2 0 0 1 18 5V6M4 9H24M6 6H22A2 2 0 0 1 24 8V19A2 2 0 0 1 22 21H6A2 2 0 0 1 4 19V8A2 2 0 0 1 6 6Z",
    check: "M5 13L9 17L19 7",
    file: "M8 4H16L21 9V22H8V4ZM16 4V9H21M11 13H18M11 17H18",
    map: "M12 21S19 14.5 19 9A7 7 0 1 0 5 9C5 14.5 12 21 12 21ZM12 11.5A2.5 2.5 0 1 0 12 6.5A2.5 2.5 0 0 0 12 11.5Z",
  };

  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 28 28"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[type]} />
    </svg>
  );
}

function JobDetailSection({ title, body }) {
  if (!body) return null;

  const bullets = sectionBullets(body);

  return (
    <section className="rounded-md border border-black/10 bg-white p-4">
      <h3 className="font-blender text-xl text-baseGray">{title}</h3>
      {bullets.length ? (
        <ul className="mt-2 list-disc space-y-1.5 pl-5 font-sans text-base font-medium leading-7 text-black/80">
          {bullets.map((item, index) => (
            <li key={`${title}-${index}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 whitespace-pre-line font-sans text-base font-medium leading-7 text-black/80">
          {body}
        </p>
      )}
    </section>
  );
}

function Careers() {
  const [initialRolesCache] = useState(() => readCachedRoles());
  const [roles, setRoles] = useState(() => initialRolesCache?.roles || []);
  const [rolesLoading, setRolesLoading] = useState(!initialRolesCache);
  const [rolesError, setRolesError] = useState("");
  // This keeps the selected role in React state while the URL keeps it shareable/bookmarkable.
  const [selectedRoleIdentifier, setSelectedRoleIdentifier] = useState(
    () => new URLSearchParams(window.location.search).get("role")
  );
  const [form, setForm] = useState(initialForm);
  const [resume, setResume] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const selectedRole = getSelectedRole(roles, selectedRoleIdentifier);
  const hasSelectedRoleDetails = Boolean(
    selectedRole?.overview ||
      selectedRole?.description ||
      selectedRole?.responsibilities ||
      selectedRole?.qualifications ||
      selectedRole?.preferredQualifications ||
      selectedRole?.whyThisRole
  );

  useEffect(() => {
    let cancelled = false;

    if (initialRolesCache && !initialRolesCache.isStale) {
      return () => {
        cancelled = true;
      };
    }

    fetch(`${apiBaseUrl}/v2/careers/roles`)
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;

        const mondayRoles = Array.isArray(data.roles)
          ? data.roles
              .map((role) => {
                // Normalize API role data into the smaller shape this page renders.
                const title = roleText(role.title);
                const identifier = roleIdentifierFromTitle(title);

                if (!title || !identifier) return null;

                return {
                  title,
                  identifier,
                  location: roleText(role.location) || "United States",
                  team: "Grid Elevated",
                  overview: roleText(role.overview),
                  description: roleText(role.description),
                  responsibilities: roleText(role.responsibilities),
                  qualifications: roleText(role.qualifications),
                  preferredQualifications: roleText(role.preferredQualifications),
                  whyThisRole: roleText(role.whyThisRole),
                };
              })
              .filter(Boolean)
          : [];

        setRoles(mondayRoles);
        writeCachedRoles(mondayRoles);
        setRolesError(mondayRoles.length ? "" : "There are no open positions at this time.");
      })
      .catch(() => {
        if (!cancelled && !initialRolesCache) {
          setRolesError("Open positions could not be loaded at this time.");
        }
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });

    return () => {
      // Prevent setting state if the user leaves the page before the request finishes.
      cancelled = true;
    };
  }, [initialRolesCache]);

  useEffect(() => {
    function syncSelectedRoleFromUrl() {
      // Handles browser back/forward after changing selected roles with pushState.
      setSelectedRoleIdentifier(new URLSearchParams(window.location.search).get("role"));
    }

    window.addEventListener("popstate", syncSelectedRoleFromUrl);
    return () => window.removeEventListener("popstate", syncSelectedRoleFromUrl);
  }, []);

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function selectRole(roleIdentifier) {
    // Update the page immediately, then mirror the selection into the URL without a reload.
    setSelectedRoleIdentifier(roleIdentifier);

    const nextUrl = roleIdentifier
      ? `/careers?role=${encodeURIComponent(roleIdentifier)}`
      : "/careers";
    window.history.pushState(null, "", nextUrl);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");

    if (!resume) {
      setError("Please upload a resume.");
      return;
    }

    if (!selectedRole) {
      setError("Please select a role.");
      return;
    }

    if (resume.size > 6 * 1024 * 1024) {
      setError("Resume must be 6 MB or smaller.");
      return;
    }

    setStatus("submitting");

    try {
      // Sending the resume as JSON text is more reliable through API Gateway than multipart file bytes.
      const resumeBase64 = await fileToBase64(resume);

      const response = await fetch(`${apiBaseUrl}/v2/careers/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          country: form.country,
          city: form.city,
          workAuthorization: form.workAuthorization,
          whyWorkHere: form.whyWorkHere,
          privacyConsent: form.privacyConsent,
          role: selectedRole.title,
          resume: {
            fileName: resume.name,
            contentType: resume.type || "application/octet-stream",
            size: resume.size,
            base64: resumeBase64,
          },
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Application could not be submitted.");
      }

      setStatus("success");
      setForm(initialForm);
      setResume(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Application could not be submitted.");
      setStatus("idle");
    }
  }

  return (
    <div className="w-full h-full bg-baseWhite">
      <section className="relative h-[62vh] min-h-[520px] w-full overflow-hidden bg-baseGray px-6 pb-16 pt-28 md:px-10 laptop:px-16 large-laptop:px-24 desktop:px-42">
        <video
          autoPlay
          muted
          playsInline
          loop
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/videos/powerline_clip.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative z-20 flex h-full items-end">
          <div className="max-w-4xl">
            <p className="mb-4 font-blender text-lg uppercase tracking-[0.35em] text-secondaryAccent">
              Careers
            </p>
            <h1 className="font-blender text-[52px] leading-[54px] text-white md:text-[72px] md:leading-[74px]">
              Build grid intelligence with Grid Elevated.
            </h1>
            <p className="mt-6 max-w-3xl font-sans text-[24px] font-medium leading-10 text-white/90">
              Explore current opportunities and submit your application for review by our hiring team.
            </p>
          </div>
        </div>
      </section>

      <main className="px-6 py-10 md:px-10 laptop:px-16 large-laptop:px-24 desktop:px-42">
        <div className="grid gap-6 laptop:grid-cols-[280px_1fr]">
          <aside className="space-y-3">
            <h2 className="font-blender text-3xl text-baseGray">Open Roles</h2>

            {!rolesLoading && rolesError && roles.length === 0 && (
              <div className="rounded-md border border-secondary/20 bg-white p-4 font-sans text-base font-medium leading-7 text-black/85">
                <p className="font-semibold text-baseGray">{rolesError}</p>
                <p className="mt-2">Please check back soon for new opportunities.</p>
              </div>
            )}

            {!rolesLoading &&
              roles.map((role) => {
                const isSelected = role.identifier === selectedRole?.identifier;

                return (
                  // Make role cards read as clickable controls on mouse and keyboard focus.
                  <button
                    type="button"
                    key={role.identifier}
                    onClick={() => selectRole(role.identifier)}
                    className={`block w-full cursor-pointer rounded-md border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-secondary/40 ${
                      isSelected
                        ? "border-secondary bg-secondary/10 hover:border-secondary"
                        : "border-black/10 bg-white hover:border-secondary/50 hover:bg-secondary/5"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-blender text-xl leading-7 text-baseGray">{role.title}</h3>
                        <p className="mt-2 flex items-center gap-2 font-sans text-sm font-medium text-black/70">
                          <Icon type="briefcase" />
                          {role.team}
                        </p>
                        <p className="mt-1 flex items-center gap-2 font-sans text-sm font-medium text-black/70">
                          <Icon type="map" />
                          {role.location}
                        </p>
                      </div>
                      {isSelected && <Icon type="check" className="size-5 text-secondary" />}
                    </div>
                  </button>
                );
              })}
          </aside>

          <section
            id="application"
            className="min-w-0 rounded-md border border-black/10 bg-white p-5 shadow-sm md:p-6"
          >
            <div className="mb-5 border-b border-black/10 pb-4">
              <p className="font-blender text-sm uppercase tracking-[0.35em] text-secondary">
                Application
              </p>
              <h2 className="mt-1 font-blender text-3xl text-baseGray">
                {selectedRole?.title || "No open positions"}
              </h2>
              {selectedRole && (
                <p className="mt-2 flex items-center gap-2 font-sans text-base font-medium text-black/70">
                  <Icon type="map" />
                  {selectedRole.location}
                </p>
              )}
            </div>

            {!selectedRole && !rolesLoading ? (
              <div className="rounded-md border border-secondary/20 bg-baseWhite p-5 font-sans text-base font-medium leading-7 text-black/85">
                <p className="font-semibold text-baseGray">No open positions are currently available.</p>
                <p className="mt-2">
                  We are not accepting applications for a specific role right now. Please check back soon
                  for future opportunities.
                </p>
              </div>
            ) : status === "success" ? (
              <div className="rounded-md border border-secondary/20 bg-secondary/10 p-6">
                <Icon type="check" className="mb-3 size-9 text-secondary" />
                <h3 className="font-blender text-2xl text-baseGray">Application submitted</h3>
                <p className="mt-2 font-sans text-base font-medium leading-7 text-black/85">
                  Thanks for applying. Your application was sent to the Grid Elevated hiring team.
                </p>
                {/* Keep the primary action visibly interactive on hover/focus. */}
                <button
                  type="button"
                  className="mt-4 cursor-pointer rounded-md border border-secondary px-4 py-2 font-blender text-lg text-secondary transition-all hover:-translate-y-0.5 hover:bg-secondary/10 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-secondary/40"
                  onClick={() => setStatus("idle")}
                >
                  Submit another application
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="grid gap-5">
                {selectedRole && hasSelectedRoleDetails && (
                  <div className="grid gap-3 border-b border-black/10 pb-5">
                    <JobDetailSection
                      title="Job Overview"
                      body={selectedRole.overview || selectedRole.description}
                    />
                    <JobDetailSection title="Responsibilities" body={selectedRole.responsibilities} />
                    <JobDetailSection title="Qualifications" body={selectedRole.qualifications} />
                    <JobDetailSection
                      title="Preferred Qualifications"
                      body={selectedRole.preferredQualifications}
                    />
                    <JobDetailSection title="Why This Role" body={selectedRole.whyThisRole} />
                  </div>
                )}

                <div className="grid gap-x-5 gap-y-4 md:grid-cols-2">
                  <label className="font-sans text-sm font-semibold text-baseGray">
                    Name *
                    <input
                      className="mt-2 h-11 w-full rounded-sm border border-black/20 bg-white px-3 text-base"
                      value={form.name}
                      onChange={(event) => updateField("name", event.target.value)}
                      required
                    />
                  </label>

                  <label className="font-sans text-sm font-semibold text-baseGray">
                    Email *
                    <input
                      type="email"
                      className="mt-2 h-11 w-full rounded-sm border border-black/20 bg-white px-3 text-base"
                      value={form.email}
                      onChange={(event) => updateField("email", event.target.value)}
                      required
                    />
                  </label>

                  <label className="font-sans text-sm font-semibold text-baseGray">
                    Phone *
                    <input
                      type="tel"
                      className="mt-2 h-11 w-full rounded-sm border border-black/20 bg-white px-3 text-base"
                      value={form.phone}
                      onChange={(event) => updateField("phone", event.target.value)}
                      required
                    />
                  </label>

                  <label className="font-sans text-sm font-semibold text-baseGray">
                    Country *
                    <input
                      className="mt-2 h-11 w-full rounded-sm border border-black/20 bg-white px-3 text-base"
                      value={form.country}
                      onChange={(event) => updateField("country", event.target.value)}
                      required
                    />
                  </label>

                  <label className="font-sans text-sm font-semibold text-baseGray">
                    City *
                    <input
                      className="mt-2 h-11 w-full rounded-sm border border-black/20 bg-white px-3 text-base"
                      value={form.city}
                      onChange={(event) => updateField("city", event.target.value)}
                      required
                    />
                  </label>

                  <label className="font-sans text-sm font-semibold text-baseGray">
                    Work Authorization *
                    <select
                      className="mt-2 h-11 w-full rounded-sm border border-black/20 bg-white px-3 text-base"
                      value={form.workAuthorization}
                      onChange={(event) => updateField("workAuthorization", event.target.value)}
                      required
                    >
                      <option value="">Select one</option>
                      <option value="yes">Yes, I am legally authorized to work in the U.S.</option>
                      <option value="no">No</option>
                    </select>
                  </label>
                </div>

                <label className="font-sans text-sm font-semibold text-baseGray">
                  Why do you want to work here?
                  <textarea
                    className="mt-2 min-h-[140px] w-full rounded-sm border border-black/20 bg-white px-3 py-3 text-base leading-7"
                    value={form.whyWorkHere}
                    onChange={(event) => updateField("whyWorkHere", event.target.value)}
                    maxLength={3000}
                  />
                </label>

                <label className="font-sans text-sm font-semibold text-baseGray">
                  Resume *
                  <div className="mt-2 flex flex-col gap-2 tablet:flex-row tablet:items-center">
                    <input
                      type="file"
                      className="w-full max-w-md rounded-sm border border-black/20 bg-white p-3 text-base"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(event) => setResume(event.target.files?.[0] || null)}
                      required
                    />
                    <span className="text-sm font-medium text-black/65">PDF, DOC, or DOCX. Max 6 MB.</span>
                  </div>
                </label>

                <label className="flex items-start gap-3 rounded-md border border-black/10 bg-baseWhite p-4 font-sans text-sm font-medium leading-6 text-black/85">
                  <input
                    type="checkbox"
                    className="mt-1 h-5 w-5 accent-secondary"
                    checked={form.privacyConsent}
                    onChange={(event) => updateField("privacyConsent", event.target.checked)}
                    required
                  />
                  I consent to Grid Elevated storing and reviewing my application information for hiring
                  purposes.
                </label>

                {error && (
                  <div className="rounded-md border border-red-300 bg-red-50 p-4 font-sans text-sm font-semibold text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="flex w-fit cursor-pointer items-center rounded-md bg-secondary px-4 py-2 font-blender text-lg text-white transition-all hover:-translate-y-0.5 hover:bg-secondary/90 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-secondary/40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60 disabled:shadow-none"
                  disabled={status === "submitting" || !selectedRole}
                >
                  {status === "submitting" ? "Submitting..." : "Submit Application"}
                  <Icon type="arrow" className="ml-2 size-5" />
                </button>
              </form>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Careers;
